import Phaser from 'phaser';

const SKY_TOP = 0x0b0c2b;
const SKY_MID = 0x3b1e6e;
const SKY_BOT = 0xd97706;

const PALACE_DARK = 0x4c3a14;
const PALACE_MAIN = 0xb8860b;
const PALACE_LIGHT = 0xfbbf24;
const WINDOW = 0xfff4c2;

const BIFROST_COLORS = [0xef4444, 0xfb923c, 0xfbbf24, 0x22c55e, 0x3b82f6, 0xa855f7];

export function drawAsgardSkyBackground(g: Phaser.GameObjects.Graphics, w: number, h: number) {
  g.clear();
  const step = Math.max(1, Math.floor(h / 40));
  for (let y = 0; y < h; y += step) {
    const t = y / h;
    let r: number, gC: number, b: number;
    if (t < 0.55) {
      const u = t / 0.55;
      r = lerp((SKY_TOP >> 16) & 0xff, (SKY_MID >> 16) & 0xff, u);
      gC = lerp((SKY_TOP >> 8) & 0xff, (SKY_MID >> 8) & 0xff, u);
      b = lerp(SKY_TOP & 0xff, SKY_MID & 0xff, u);
    } else {
      const u = (t - 0.55) / 0.45;
      r = lerp((SKY_MID >> 16) & 0xff, (SKY_BOT >> 16) & 0xff, u);
      gC = lerp((SKY_MID >> 8) & 0xff, (SKY_BOT >> 8) & 0xff, u);
      b = lerp(SKY_MID & 0xff, SKY_BOT & 0xff, u);
    }
    g.fillStyle((r << 16) | (gC << 8) | b, 1);
    g.fillRect(0, y, w, step + 1);
  }
}

export function drawAsgardSilhouette(
  g: Phaser.GameObjects.Graphics,
  w: number,
  h: number,
  seed = 101,
) {
  const rand = mulberry32(seed);
  const baselineY = Math.floor(h * 0.78);
  const palaceW = Math.min(w * 0.5, 420);
  const palaceX = Math.floor((w - palaceW) / 2);

  g.fillStyle(PALACE_DARK, 1);
  const foregroundH = h - baselineY;
  g.fillRect(0, baselineY + Math.floor(foregroundH * 0.5), w, h - baselineY);

  const sideTowerW = palaceW * 0.2;
  const sideTowerH = Math.floor(foregroundH * 1.1);
  g.fillStyle(PALACE_MAIN, 1);
  g.fillRect(palaceX, baselineY - sideTowerH * 0.85, sideTowerW, sideTowerH);
  g.fillRect(palaceX + palaceW - sideTowerW, baselineY - sideTowerH * 0.85, sideTowerW, sideTowerH);

  const centerW = palaceW * 0.38;
  const centerH = Math.floor(foregroundH * 1.55);
  const centerX = palaceX + (palaceW - centerW) / 2;
  const centerY = baselineY - centerH * 0.9;
  g.fillStyle(PALACE_MAIN, 1);
  g.fillRect(centerX, centerY, centerW, centerH);

  const domeR = centerW * 0.55;
  g.fillStyle(PALACE_LIGHT, 1);
  g.beginPath();
  g.arc(centerX + centerW / 2, centerY + 2, domeR, Math.PI, 0, false);
  g.closePath();
  g.fillPath();

  g.fillStyle(PALACE_LIGHT, 1);
  g.fillRect(centerX + centerW / 2 - 2, centerY - 20, 4, 20);
  g.fillCircle(centerX + centerW / 2, centerY - 22, 4);

  const windows = Math.max(3, Math.floor(centerH / 20));
  for (let i = 0; i < windows; i++) {
    if (rand() < 0.3) continue;
    g.fillStyle(WINDOW, 1);
    g.fillRect(centerX + centerW / 2 - 2, centerY + 20 + i * 20, 4, 8);
  }
  const sideWindows = Math.max(3, Math.floor(sideTowerH / 18));
  for (let t = 0; t < 2; t++) {
    const tx = t === 0 ? palaceX + sideTowerW / 2 : palaceX + palaceW - sideTowerW / 2;
    for (let i = 0; i < sideWindows; i++) {
      if (rand() < 0.4) continue;
      g.fillStyle(WINDOW, 1);
      g.fillRect(tx - 2, baselineY - sideTowerH * 0.8 + i * 18, 4, 6);
    }
  }

  drawBifrost(g, w, h);
}

function drawBifrost(g: Phaser.GameObjects.Graphics, w: number, h: number) {
  const bandTop = Math.floor(h * 0.93);
  const bandH = h - bandTop;
  const stripeH = bandH / BIFROST_COLORS.length;
  for (let i = 0; i < BIFROST_COLORS.length; i++) {
    g.fillStyle(BIFROST_COLORS[i], 0.9);
    g.fillRect(0, bandTop + i * stripeH, w, stripeH + 1);
  }
  g.fillStyle(0xffffff, 0.25);
  g.fillRect(0, bandTop, w, 2);
}

function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
